/**
 * The brief step on the Plan page. A plan draft is the intake of a change;
 * "Shape the brief" is where the agent writes the four parts for the owner's
 * review, and the board is populated from plan.md on approval one — never
 * before. The step is a preview: the panel shows exactly what the brief of
 * this plan would be; populating the board directly stays as it is until
 * CHG-11 gates it on the brief (ADR-218).
 */
import { useState } from 'react';
import { ChevronDown, ChevronRight, FileText, ScrollText, TestTubes, ClipboardList } from 'lucide-react';
import { cn } from '@/lib/utils';
import {
  APPROVALS,
  PART_LABEL,
  SIZES,
  SIZE_RULE,
  requiredParts,
  type ChangeSize,
  type PartKey,
} from '@/lib/changes/model';

/** "Customer reminders" → "customer-reminders": the change folder's name. */
export function slugify(name: string): string {
  return name
    .toLowerCase()
    .normalize('NFKD')
    .replace(/[̀-ͯ]/g, '')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 60) || 'change';
}

const PART_ICON: Record<PartKey, typeof FileText> = {
  decision: ScrollText,
  contract: FileText,
  behaviour: TestTubes,
  plan: ClipboardList,
};

/** The files each part of the brief becomes, for a change of this slug. */
export function briefFiles(slug: string, part: PartKey, counts: { epics: number; stories: number }): string {
  switch (part) {
    case 'decision':
      return `docs/adr/ADR-nnn-${slug}.md`;
    case 'contract':
      return `specs/changes/${slug}/openapi.yaml · asyncapi.yaml · contract.md · proposal.md`;
    case 'behaviour':
      return `features/${slug}.feature — every scenario @proposed`;
    case 'plan':
      return `specs/changes/${slug}/plan.md — ${counts.epics} epics, ${counts.stories} stories, one pull request in one repository each`;
  }
}

export function ShapeBriefPanel({
  productName,
  epics,
  stories,
}: {
  productName: string;
  epics: number;
  stories: number;
}) {
  const [open, setOpen] = useState(false);
  const [size, setSize] = useState<ChangeSize>('ordinary');
  const slug = slugify(productName);
  const parts = requiredParts(size);

  return (
    <div className="rounded-lg border border-violet-200 bg-violet-50/40 dark:border-violet-900/60 dark:bg-violet-950/20">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        className="flex w-full items-center gap-2 px-3 py-2 text-left"
      >
        {open ? <ChevronDown className="h-4 w-4 text-violet-600 dark:text-violet-300" /> : <ChevronRight className="h-4 w-4 text-violet-600 dark:text-violet-300" />}
        <span className="text-xs font-semibold text-violet-800 dark:text-violet-200">Shape the brief</span>
        <span className="text-xs text-slate-500 dark:text-slate-400">
          — the four parts the owner approves in one sitting; the board follows from plan.md
        </span>
      </button>

      {open && (
        <div className="space-y-3 border-t border-violet-200 px-3 py-3 dark:border-violet-900/60">
          <div>
            <p className="mb-1.5 text-[10px] font-medium uppercase tracking-wider text-slate-500">
              Size — the agent proposes, you may change it in a word
            </p>
            <div className="flex flex-col gap-1.5 sm:flex-row">
              {SIZES.map((s) => (
                <label
                  key={s}
                  className={cn(
                    'flex flex-1 cursor-pointer items-start gap-2 rounded-md border p-2 text-xs',
                    size === s
                      ? 'border-violet-400 bg-white dark:border-violet-500 dark:bg-slate-900'
                      : 'border-slate-200 bg-white/60 dark:border-slate-700 dark:bg-slate-900/60',
                  )}
                >
                  <input
                    type="radio"
                    name="brief-size"
                    value={s}
                    checked={size === s}
                    onChange={() => setSize(s)}
                    className="mt-0.5"
                  />
                  <span>
                    <span className="font-semibold capitalize text-slate-800 dark:text-slate-200">{s}</span>
                    <span className="block text-slate-500 dark:text-slate-400">{SIZE_RULE[s]}</span>
                  </span>
                </label>
              ))}
            </div>
          </div>

          <div>
            <p className="mb-1.5 text-[10px] font-medium uppercase tracking-wider text-slate-500">
              The brief this plan becomes — <code className="font-mono normal-case">{slug}</code>
            </p>
            <ul className="space-y-1">
              {parts.map((part) => {
                const Icon = PART_ICON[part];
                return (
                  <li key={part} data-part={part} className="flex items-start gap-2 text-xs">
                    <Icon className="mt-0.5 h-3.5 w-3.5 shrink-0 text-violet-600 dark:text-violet-300" />
                    <span>
                      <span className="font-medium text-slate-800 dark:text-slate-200">{PART_LABEL[part]}:</span>{' '}
                      <code className="font-mono text-[11px] text-slate-600 dark:text-slate-400">{briefFiles(slug, part, { epics, stories })}</code>
                    </span>
                  </li>
                );
              })}
              {size === 'small' && (
                <li data-part="decision" className="text-xs text-slate-500 dark:text-slate-400">
                  No decision record: the change is small.
                </li>
              )}
            </ul>
          </div>

          <p className="text-xs text-slate-600 dark:text-slate-400">
            <span className="font-medium text-violet-800 dark:text-violet-200">{APPROVALS.one.label}, {APPROVALS.one.what}.</span>{' '}
            {APPROVALS.one.how} The board is populated from plan.md then, never before.
          </p>

          <p data-today className="text-xs text-slate-500 dark:text-slate-400">
            Today the plan populates the board directly, and drafts stay in this browser.
          </p>
        </div>
      )}
    </div>
  );
}
